/**
 * Docs viewer page — renders markdown files from the repo-root docs/ folder.
 *
 * Content is synced into `public/docs-content/` by a Vite plugin at build
 * time (see vite.config.ts). At runtime the page fetches the manifest +
 * individual markdown files as static assets.
 *
 * @module pages/DocsPage
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ChevronRight, FileText, Folder, FolderOpen, BookOpen } from 'lucide-react';
import { clsx } from 'clsx';
import { PageHeader } from '@/components/layout';

interface DocFileNode {
  type: 'file';
  name: string;
  path: string;
}

interface DocDirNode {
  type: 'dir';
  name: string;
  path: string;
  children: DocNode[];
}

type DocNode = DocFileNode | DocDirNode;

const CONTENT_BASE = '/docs-content';

/** Strip the .md extension and prettify the filename for sidebar display. */
function prettyName(name: string): string {
  return name.replace(/\.md$/i, '');
}

/** Recursive sidebar tree node. */
function TreeNode({
  node,
  activePath,
  depth = 0,
  defaultExpanded,
}: {
  node: DocNode;
  activePath: string | undefined;
  depth?: number;
  defaultExpanded: boolean;
}) {
  const isActive = node.type === 'file' && node.path === activePath;
  // Expand if a descendant is active, or if defaultExpanded for top level.
  const containsActive =
    node.type === 'dir' && activePath !== undefined && activePath.startsWith(node.path + '/');
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded || containsActive);

  useEffect(() => {
    if (containsActive) setExpanded(true);
  }, [containsActive]);

  const indent = { paddingLeft: `${depth * 12 + 8}px` };

  if (node.type === 'file') {
    return (
      <Link
        to={`/docs/${node.path}`}
        style={indent}
        className={clsx(
          'flex items-center gap-2 py-1 pr-2 text-sm rounded transition-colors',
          isActive
            ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
        )}
      >
        <FileText className="h-3.5 w-3.5 shrink-0 opacity-60" />
        <span className="truncate">{prettyName(node.name)}</span>
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={indent}
        className="flex w-full items-center gap-2 py-1 pr-2 text-sm rounded text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <ChevronRight
          className={clsx('h-3 w-3 shrink-0 transition-transform', expanded && 'rotate-90')}
        />
        {expanded ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 opacity-60" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0 opacity-60" />
        )}
        <span className="truncate font-medium">{node.name}</span>
      </button>
      {expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              activePath={activePath}
              depth={depth + 1}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Resolve a relative URL from a markdown file against its containing folder,
 * returning a Portal-absolute URL (or an external URL unchanged).
 */
function resolveDocUrl(url: string, docPath: string): string {
  if (!url) return url;
  // Leave anchors, absolute URLs, mailto, data, etc. alone.
  if (/^(?:[a-z]+:|#|\/\/)/i.test(url) || url.startsWith('/')) return url;

  const docDir = docPath.includes('/') ? docPath.replace(/\/[^/]*$/, '/') : '';
  const segments = (docDir + url).split('/');
  const resolved: string[] = [];
  for (const seg of segments) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      resolved.pop();
      continue;
    }
    resolved.push(seg);
  }
  const joined = resolved.join('/');

  // Markdown links to other docs → internal /docs/ routes; static assets
  // (images, etc.) → the static /docs-content/ tree.
  if (joined.toLowerCase().endsWith('.md')) {
    return `/docs/${joined}`;
  }
  return `${CONTENT_BASE}/${joined}`;
}

/** Markdown viewer: fetches and renders a single doc. */
function DocViewer({ docPath }: { docPath: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(null);

    fetch(`${CONTENT_BASE}/${docPath}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancelled) setContent(text);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [docPath]);

  if (error) {
    return (
      <div className="p-6 text-sm text-danger-600 dark:text-danger-500">
        Failed to load document: {error}
      </div>
    );
  }

  if (content === null) {
    return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  return (
    <article className="prose prose-sm dark:prose-invert max-w-none p-6 prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-headings:scroll-mt-20">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        urlTransform={(url) => resolveDocUrl(url, docPath)}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}

/** Top-level Docs page (handles index + per-doc routing via /docs/*). */
export function DocsPage() {
  const params = useParams();
  const navigate = useNavigate();
  // Everything after /docs/ is the doc path (e.g. "arch-design/NodeArch.md").
  const splat = params['*'] ?? '';
  const docPath = splat.endsWith('.md') ? splat : '';

  const [manifest, setManifest] = useState<DocDirNode | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${CONTENT_BASE}/_manifest.json`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as DocDirNode;
      })
      .then((m) => {
        if (!cancelled) setManifest(m);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setManifestError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick a sensible default doc when landing on /docs with no selection.
  const defaultDocPath = useMemo(() => {
    if (!manifest) return '';
    // Prefer the repo README equivalent: faq.md or the first top-level .md.
    const topFiles = manifest.children.filter((c): c is DocFileNode => c.type === 'file');
    const preferred = topFiles.find((f) => f.name.toLowerCase() === 'faq.md') ?? topFiles[0];
    return preferred?.path ?? '';
  }, [manifest]);

  useEffect(() => {
    if (!docPath && defaultDocPath) {
      navigate(`/docs/${defaultDocPath}`, { replace: true });
    }
  }, [docPath, defaultDocPath, navigate]);

  return (
    <div>
      <PageHeader title="Docs" description="Architecture, design, and operational documentation" />

      <div className="flex gap-4 min-h-[60vh]">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 border-r border-gray-200 dark:border-gray-800 pr-2 overflow-y-auto max-h-[calc(100vh-12rem)] sticky top-4">
          {manifestError && (
            <div className="text-xs text-danger-600 dark:text-danger-500 p-2">
              Failed to load docs index: {manifestError}
            </div>
          )}
          {!manifest && !manifestError && (
            <div className="text-xs text-gray-500 dark:text-gray-400 p-2">Loading…</div>
          )}
          {manifest && (
            <nav className="py-2">
              {manifest.children.length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 p-2 flex items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5" />
                  No docs available
                </div>
              )}
              {manifest.children.map((node) => (
                <TreeNode
                  key={node.path}
                  node={node}
                  activePath={docPath}
                  defaultExpanded={node.type === 'dir' && node.children.length <= 12}
                />
              ))}
            </nav>
          )}
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 bg-white dark:bg-gray-900/30 rounded border border-gray-200 dark:border-gray-800">
          {docPath ? (
            <DocViewer docPath={docPath} />
          ) : (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              Select a document from the sidebar.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
